import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

const API_URL = config.API_URL;
const STORAGE_ACCOUNT = "https://driverwatchstorage.blob.core.windows.net/tools";

const MANUFACTURERS = [
  { key: "hp", name: "HP", tool_name: "HP Image Assistant (HPIA)", color: "bg-blue-900 text-blue-300", docs: "https://ftp.hp.com/pub/caps-softpaq/cmit/HPIA.html" },
  { key: "dell", name: "Dell", tool_name: "Dell Command Update", color: "bg-green-900 text-green-300", docs: "https://www.dell.com/support/kbdoc/en-us/000177325/dell-command-update" },
  { key: "lenovo", name: "Lenovo", tool_name: "Lenovo System Update", color: "bg-red-900 text-red-300", docs: "https://support.lenovo.com/us/en/solutions/ht003029" }
];

export default function Tools() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("internal_token");
  const storedUser = JSON.parse(sessionStorage.getItem("internal_user") || "{}");
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [version, setVersion] = useState("");
  const [file, setFile] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchTools();
  }, []);

  async function fetchTools() {
    try {
      const res = await axios.get(`${API_URL}/internal/tools`, authHeaders);
      setTools(res.data);
    } catch (err) {
      console.error("Failed to load tools", err);
    } finally {
      setLoading(false);
    }
  }

  function getToolInfo(manufacturerKey) {
    return tools.find(t => t.manufacturer === manufacturerKey);
  }

  async function handleUpload(manufacturerKey) {
    if (!version.trim() || !file) { setErrorMsg("Please enter a version number and select a file."); return; }
    setUploading(manufacturerKey);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const filename = file.name;
      const blobUrl = `${STORAGE_ACCOUNT}/${manufacturerKey}/${filename}`;
      const manufacturer = MANUFACTURERS.find(m => m.key === manufacturerKey);
      const fileSize = formatFileSize(file.size);
      await axios.post(
        `${API_URL}/internal/tools?manufacturer=${manufacturerKey}&tool_name=${encodeURIComponent(manufacturer.tool_name)}&version=${encodeURIComponent(version)}&blob_url=${encodeURIComponent(blobUrl)}&file_size=${encodeURIComponent(fileSize)}&uploaded_by=${encodeURIComponent(storedUser.email)}`,
        {},
        authHeaders
      );
      setSuccessMsg(`Tool registered successfully. Upload the file to Azure Blob Storage at: tools/${manufacturerKey}/${filename}`);
      setVersion("");
      setFile(null);
      setSelectedManufacturer(null);
      fetchTools();
    } catch (err) {
      setErrorMsg("Failed to register tool. Please try again.");
    } finally {
      setUploading(null);
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function handleDelete(toolId, toolName) {
    if (!window.confirm(`Remove ${toolName}?`)) return;
    try {
      await axios.delete(`${API_URL}/internal/tools/${toolId}`, authHeaders);
      fetchTools();
    } catch (err) {
      alert("Failed to remove tool.");
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <nav className="bg-gray-900 border-b border-red-900 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src="/logo-symbol.svg" alt="AleroSys" className="h-8 w-8" />
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">AleroSys</span>
            <span className="text-gray-500 text-lg font-light">—</span>
            <span className="text-red-400 text-lg font-semibold">DriverWatch Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-gray-400 hover:text-white text-sm transition-colors">Back</button>
          <button onClick={() => navigate("/users")} className="text-gray-400 hover:text-white text-sm border border-gray-700 px-3 py-1 rounded-lg transition-colors">Staff</button>
          <button onClick={() => navigate("/feature-requests")} className="text-gray-400 hover:text-white text-sm border border-gray-700 px-3 py-1 rounded-lg transition-colors">Feature Requests</button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">Tools Management</h1>
        <p className="text-gray-400 text-sm mb-8">Manage manufacturer remediation tools hosted in Azure Blob Storage.</p>

        {successMsg && <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg mb-6 text-sm">{successMsg}</div>}
        {errorMsg && <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">{errorMsg}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {MANUFACTURERS.map(mfr => {
            const toolInfo = getToolInfo(mfr.key);
            return (
              <div key={mfr.key} className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${mfr.color}`}>{mfr.name}</span>
                  {toolInfo && <span className="text-gray-500 text-xs">v{toolInfo.version}</span>}
                </div>
                <h3 className="text-white font-medium text-sm mb-2">{mfr.tool_name}</h3>
                {toolInfo ? (
                  <div className="mb-4">
                    <p className="text-gray-500 text-xs mb-1">Size: {toolInfo.file_size}</p>
                    <p className="text-gray-500 text-xs mb-1">By: {toolInfo.uploaded_by}</p>
                    <p className="text-gray-500 text-xs mb-2">Updated: {new Date(toolInfo.uploaded_at).toLocaleDateString()}</p>
                    <code className="text-green-400 text-xs bg-gray-800 px-2 py-1 rounded block break-all">{toolInfo.blob_url}</code>
                  </div>
                ) : (
                  <p className="text-gray-600 text-xs mb-4">No version registered yet.</p>
                )}
                <div className="flex gap-2 mt-auto">
                  <button
                    onClick={() => { setSelectedManufacturer(mfr.key); setVersion(""); setFile(null); setSuccessMsg(""); setErrorMsg(""); }}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                  >
                    {toolInfo ? "Update" : "Register"}
                  </button>
                  {toolInfo && (
                    <button
                      onClick={() => handleDelete(toolInfo.id, mfr.tool_name)}
                      className="bg-red-900 hover:bg-red-800 text-red-300 text-xs px-3 py-1.5 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <a href={mfr.docs} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 text-xs mt-3 block">
                  Manufacturer docs
                </a>
              </div>
            );
          })}
        </div>

        {selectedManufacturer && (
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 mb-8">
            <h2 className="text-white font-semibold mb-4">Register {MANUFACTURERS.find(m => m.key === selectedManufacturer)?.tool_name}</h2>
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">Version Number</label>
              <input
                type="text"
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder="e.g. 5.1.0"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-1">Installer File</label>
              <input
                type="file"
                accept=".exe"
                onChange={e => setFile(e.target.files[0])}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              />
              {file && <p className="text-gray-500 text-xs mt-1">{file.name} - {formatFileSize(file.size)}</p>}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleUpload(selectedManufacturer)}
                disabled={uploading === selectedManufacturer}
                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
              >
                {uploading === selectedManufacturer ? "Registering..." : "Register Tool"}
              </button>
              <button onClick={() => setSelectedManufacturer(null)} className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-5 py-2 rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-3">Blob Storage Structure</h2>
          <div className="bg-gray-800 rounded-lg p-4 font-mono text-xs text-green-400">
            <p>tools/hp/HPImageAssistant.exe</p>
            <p>tools/dell/DellCommandUpdate.exe</p>
            <p>tools/lenovo/LenovoSystemUpdate.exe</p>
          </div>
        </div>
      </div>
    </div>
  );
}
